import { useEffect, useMemo, useState, type CSSProperties, type ReactElement } from 'react';
import { v2, type TemplateVersion, type TemplateVersionDiff } from '@/api/v2Client';
import type { DiffLine } from '@/lib/templateRender';
import { formatApiError } from '@/lib/errors';
import { templateVersionLabel } from '@/lib/templateVersion';
import { CURRENT_VERSION, previousVersionId } from '@/lib/versionCompare';
import { DiffLineList } from './DiffLineList';

/**
 * Form Templates tracker row 8 — compare two VERSIONS of one template.
 *
 * Used in the version history (FormTemplatesPage's history modal and the
 * template detail page) and inside the Restore confirmation, so a user can see
 * what a restore replaces before confirming. The diff itself is computed by
 * the server (GET …/versions/:a/diff/:b); lines render through the shared
 * DiffLineList. Three states, each explicit:
 *   - additions only → green lines, no removals section
 *   - removals only  → red lines, no additions section
 *   - identical      → "These versions are identical", no empty diff box
 */
const CURRENT = CURRENT_VERSION;

export interface VersionDiffPanelProps {
  readonly templateId: string;
  /** The template's versions (any order). */
  readonly versions: readonly TemplateVersion[];
  /** The "To" side. Defaults to the newest version. */
  readonly initialRight?: string;
  /** The "From" side. Defaults to the version just before `initialRight`. */
  readonly initialLeft?: string;
  readonly fromLabel?: string;
  readonly toLabel?: string;
  /** Restore confirmations must describe the exact action, without editable sides. */
  readonly readOnly?: boolean;
}

function sideLabel(versions: readonly TemplateVersion[], id: string): string {
  if (id === CURRENT) return 'Current (live) text';
  const v = versions.find((x) => x.Id === id);
  return v ? `Version ${templateVersionLabel(v.VersionNumber)}` : 'Version';
}

export function VersionDiffPanel({
  templateId,
  versions,
  initialRight,
  initialLeft,
  fromLabel = 'From',
  toLabel = 'To',
  readOnly = false,
}: VersionDiffPanelProps): ReactElement {
  const sorted = useMemo(() => [...versions].sort((a, b) => b.VersionNumber - a.VersionNumber), [versions]);
  const [selectedRight, setRight] = useState<string>(initialRight ?? sorted[0]?.Id ?? CURRENT);
  const [selectedLeft, setLeft] = useState<string>(initialLeft ?? previousVersionId(sorted, initialRight ?? sorted[0]?.Id ?? ''));
  const right = readOnly ? initialRight ?? sorted[0]?.Id ?? CURRENT : selectedRight;
  const left = readOnly ? initialLeft ?? previousVersionId(sorted, right) : selectedLeft;
  const [diff, setDiff] = useState<TemplateVersionDiff | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDiff(null);
    setError(null);
    v2.documentTemplates
      .versionDiff(templateId, left, right)
      .then((d) => {
        if (!cancelled) setDiff(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(formatApiError(e, 'Could not compare these versions.'));
      });
    return (): void => {
      cancelled = true;
    };
  }, [templateId, left, right]);

  const picker = (label: string, value: string, onChange: (v: string) => void, testId: string): ReactElement => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      {readOnly ? (
        <span data-testid={testId} aria-label={`${label} version`}>{sideLabel(sorted, value)}</span>
      ) : <select
        data-testid={testId}
        aria-label={`${label} version`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 12 }}
      >
        <option value={CURRENT}>Current (live) text</option>
        {sorted.map((v) => (
          <option key={v.Id} value={v.Id}>
            Version {templateVersionLabel(v.VersionNumber)}
            {v.ChangeNote ? ` — ${v.ChangeNote}` : ''}
          </option>
        ))}
      </select>}
    </label>
  );

  const added: DiffLine[] = [];
  const removed: DiffLine[] = [];
  const unified: DiffLine[] = [];
  for (const h of diff?.body.hunks ?? []) {
    for (const text of h.lines) {
      const type = h.type === 'add' ? 'added' : h.type === 'remove' ? 'removed' : 'equal';
      unified.push({ text, type });
      if (type === 'added') added.push({ text, type });
      if (type === 'removed') removed.push({ text, type });
    }
  }

  return (
    <div data-testid="version-diff-panel" style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
        {picker(fromLabel, left, setLeft, 'version-diff-from')}
        {picker(toLabel, right, setRight, 'version-diff-to')}
      </div>

      {error && (
        <div role="alert" data-testid="version-diff-error" style={{ color: 'var(--red)', fontSize: 13 }}>
          {error}
        </div>
      )}
      {!error && !diff && (
        <div data-testid="version-diff-loading" style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Comparing…
        </div>
      )}

      {diff && diff.identical && (
        <div data-testid="version-diff-identical" style={identicalStyle}>
          ✓ These versions are identical — {sideLabel(sorted, left)} and {sideLabel(sorted, right)} have the same
          text.
          {(diff.leftVersion.changeNote ?? '') !== (diff.rightVersion.changeNote ?? '') && (
            <span style={{ display: 'block', marginTop: 4, color: 'var(--text-muted)' }}>
              Only the change note differs
              {diff.rightVersion.changeNote ? ` (“${diff.rightVersion.changeNote}”)` : ''}.
            </span>
          )}
        </div>
      )}

      {diff && !diff.identical && (
        <div data-testid="version-diff-result">
          <div data-testid="version-diff-summary" style={{ fontSize: 12, marginBottom: 8 }}>
            {sideLabel(sorted, left)} → {sideLabel(sorted, right)}:{' '}
            {diff.body.added > 0 && (
              <span style={{ color: '#166534', fontWeight: 700 }}>
                +{diff.body.added} line{diff.body.added === 1 ? '' : 's'} added{' '}
              </span>
            )}
            {diff.body.removed > 0 && (
              <span style={{ color: '#991b1b', fontWeight: 700 }}>
                −{diff.body.removed} line{diff.body.removed === 1 ? '' : 's'} removed
              </span>
            )}
          </div>
          {added.length > 0 && (
            <section data-testid="version-diff-added" style={sectionStyle('#bbf7d0')}>
              <div style={headStyle('#166534')}>Added ({added.length})</div>
              <DiffLineList lines={added} addedBg="#dcfce7" maxHeight="20vh" />
            </section>
          )}
          {removed.length > 0 && (
            <section data-testid="version-diff-removed" style={sectionStyle('#fecaca')}>
              <div style={headStyle('#991b1b')}>Removed ({removed.length})</div>
              <DiffLineList lines={removed} maxHeight="20vh" />
            </section>
          )}
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
              Full text with changes in place
            </summary>
            <DiffLineList lines={unified} addedBg="#dcfce7" maxHeight="30vh" testId="version-diff-unified" />
          </details>
          {(diff.variables.addedInRight.length > 0 || diff.variables.removedInRight.length > 0) && (
            <div data-testid="version-diff-variables" style={{ fontSize: 12, marginTop: 8, color: 'var(--text-muted)' }}>
              {diff.variables.addedInRight.length > 0 && <>Variables added: {diff.variables.addedInRight.join(', ')}. </>}
              {diff.variables.removedInRight.length > 0 && <>Variables removed: {diff.variables.removedInRight.join(', ')}.</>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const identicalStyle: CSSProperties = {
  padding: 10,
  background: 'var(--green-bg, #dcfce7)',
  border: '1px solid #bbf7d0',
  borderRadius: 6,
  color: 'var(--green, #166534)',
  fontSize: 13,
};
const sectionStyle = (border: string): CSSProperties => ({
  border: `1px solid ${border}`,
  borderRadius: 6,
  marginBottom: 8,
  overflow: 'hidden',
});
const headStyle = (color: string): CSSProperties => ({
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color,
  padding: '6px 10px 0',
});
